<!-- App.svelte -->
<script>
  import { onDestroy, onMount } from "svelte";
  import Icon from "@iconify/svelte";
  import { t } from "./lib/i18n";
  import LanguageSwitcher from "./components/LanguageSwitcher.svelte";

  // Top nav icons
  import resultsIcon from "@iconify/icons-mdi/clipboard-text-outline";
  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import apiIcon from "@iconify/icons-mdi/api";
  import storageIcon from "@iconify/icons-mdi/folder-outline";
  import webhookIcon from "@iconify/icons-mdi/webhook";
  import runtimeIcon from "@iconify/icons-mdi/monitor-dashboard";
  import tasksIcon from "@iconify/icons-mdi/format-list-checks";

  // Controls icons
  import pauseIcon from "@iconify/icons-mdi/pause";
  import playIcon from "@iconify/icons-mdi/play";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import logoutIcon from "@iconify/icons-mdi/logout";
  import menuIcon from "@iconify/icons-mdi/menu";
  import closeIcon from "@iconify/icons-mdi/close";

  import Dashboard from "./routes/Dashboard.svelte";
  import Results from "./routes/Results.svelte";
  import Settings from "./routes/Settings.svelte";
  import Api from "./routes/Api.svelte";
  import Storage from "./routes/Storage.svelte";
  import Webhooks from "./routes/Webhooks.svelte";
  import Runtime from "./routes/Runtime.svelte";
  import Login from "./routes/Login.svelte";
  import Tasks from "./routes/Tasks.svelte";

  const DEV_PORTS = new Set(["5173", "4173"]);
  const isViteDev =
    location.hostname === "localhost" && DEV_PORTS.has(location.port);

  const API_BASE = isViteDev
    ? (import.meta.env.VITE_API_BASE ?? "http://localhost:8080")
    : "";

  const JSON_HEADERS = { Accept: "application/json" };
  const STORAGE_KEY = "monocle_api_key";

  // ─────────────────────────────────────────────
  // Auth mode (boot + runtime promotion)
  // ─────────────────────────────────────────────
  const AUTH_MODE_BOOT = (() => {
    if (typeof document === "undefined") return false;
    const s = document.querySelector('script[type="module"][data-authenticate]');
    const raw = (s?.dataset?.authenticate || "")
      .toString()
      .trim()
      .toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes" || raw === "on";
  })();

  let authMode = AUTH_MODE_BOOT;

  // ─────────────────────────────────────────────
  // App state
  // ─────────────────────────────────────────────
  let screen = "dashboard";
  let menuOpen = false;
  const screens = new Set(["dashboard", "results", "storage", "webhooks", "api", "tasks", "settings", "runtime"]);

  // Topbar controls (owned by App)
  let dashConnected = false;
  let dashPaused = false;
  let dashRefresh = null;
  let connectionTimer = null;
  let connectionInflight = null;

  // Prefetched setup (static)
  let setup = null;
  let setupErr = "";

  // Auth state
  let apiKey = "";
  let authed = !authMode;
  let booted = false;
  let pausedByAuth = false;

  // Login-visible error (must persist while on Login screen)
  let authErr = "";

  // ─────────────────────────────────────────────
  // Auth helpers
  // ─────────────────────────────────────────────
  function readKey() {
    try {
      return (localStorage.getItem(STORAGE_KEY) || "").trim();
    } catch {
      return "";
    }
  }

  function setKey(next) {
    const v = (next || "").trim();
    try {
      if (!v) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, v);
      window.dispatchEvent(new Event("monocle-auth-changed"));
    } catch {}
  }

  function syncAuthFromStorage() {
    apiKey = readKey();
    authed = !authMode || apiKey !== "";

    if (!authed) {
      pausedByAuth = true;
      dashPaused = true;
      dashConnected = false;
      return;
    }

    if (pausedByAuth) {
      dashPaused = false;
      pausedByAuth = false;
    }
  }

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  async function pollConnection() {
    if (authMode && !apiKey) {
      dashConnected = false;
      return;
    }

    try {
      connectionInflight?.abort?.();
    } catch {}
    connectionInflight = new AbortController();

    try {
      const res = await fetch(`${API_BASE}/`, {
        method: "GET",
        cache: "no-store",
        signal: connectionInflight.signal,
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(apiKey ? "Invalid API key" : "Authentication required");
        return;
      }

      dashConnected = res.ok;
    } catch (e) {
      if (e?.name === "AbortError") return;
      dashConnected = false;
    }
  }

  function startConnectionPolling() {
    if (connectionTimer) return;
    pollConnection();
    connectionTimer = setInterval(pollConnection, 2500);
  }

  function stopConnectionPolling() {
    if (!connectionTimer) return;
    clearInterval(connectionTimer);
    connectionTimer = null;
    try {
      connectionInflight?.abort?.();
    } catch {}
  }

  function onUnauthorized(msg = "Authentication required") {
    authErr = msg;
    authMode = true;
    authed = false;
    pausedByAuth = true;
    dashPaused = true;
    dashConnected = false;
    setup = null;
    setupErr = "";
    screen = "dashboard";
    setKey("");
    apiKey = "";
  }

  // ─────────────────────────────────────────────
  // Navigation / controls
  // ─────────────────────────────────────────────
  function screenFromHash() {
    const path = String(window.location.hash || "").replace(/^#\/?/, "").trim();
    const [name] = path.split("/").filter(Boolean);
    return screens.has(name) ? name : "dashboard";
  }

  function go(to) {
    const next = screens.has(to) ? to : "dashboard";
    screen = next;
    menuOpen = false;
    const nextHash = `#${next}`;
    if (window.location.hash !== nextHash) window.location.hash = nextHash;
  }

  function doRefresh() {
    if (typeof dashRefresh === "function") dashRefresh();
  }

  function togglePause() {
    dashPaused = !dashPaused;
  }

  // ─────────────────────────────────────────────
  // Setup fetch
  // ─────────────────────────────────────────────
  async function prefetchSetup() {
    setupErr = "";
    try {
      const res = await fetch(`${API_BASE}/setup`, {
        method: "GET",
        cache: "no-store",
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(apiKey ? "Invalid API key" : "Authentication required");
        return;
      }

      if (!res.ok) throw new Error(`setup failed: ${res.status}`);

      setup = await res.json();

      if (authMode && apiKey) authed = true;
    } catch (e) {
      setup = null;
      setupErr = e?.message || String(e);
    }
  }

  // ─────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────
  onMount(() => {
    screen = screenFromHash();
    syncAuthFromStorage();
    booted = true;

    const onStorage = (e) => {
      if (e?.key === STORAGE_KEY) syncAuthFromStorage();
    };
    const onCustom = () => {
      syncAuthFromStorage();
      pollConnection();
    };
    const onHashChange = () => {
      screen = screenFromHash();
      menuOpen = false;
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("monocle-auth-changed", onCustom);
    window.addEventListener("hashchange", onHashChange);

    prefetchSetup();
    startConnectionPolling();

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("monocle-auth-changed", onCustom);
      window.removeEventListener("hashchange", onHashChange);
      stopConnectionPolling();
    };
  });

  onDestroy(() => {
    stopConnectionPolling();
  });

  $: if (authMode && !authed) {
    screen = "dashboard";
    setup = null;
    setupErr = "";
  }

  let didPrefetchAfterLogin = false;
  $: if (authed && !didPrefetchAfterLogin) {
    didPrefetchAfterLogin = true;
    prefetchSetup();
  }
  $: if (!authed) {
    didPrefetchAfterLogin = false;
  }

  const iconBtnBase =
    "inline-flex items-center justify-center w-10 h-10 rounded border " +
    "transition-all select-none " +
    "border-white/10 bg-white/[0.035] hover:bg-white/[0.06] " +
    "text-zinc-100/90 hover:text-white";

  const dividerCls = "w-px h-6 bg-white/10 mx-2";
</script>

<div class="min-h-screen bg-black text-zinc-100">
  <!-- Background -->
  <div class="pointer-events-none fixed inset-0 -z-10">
    <div class="absolute inset-0 bg-[radial-gradient(1200px_560px_at_15%_-10%,rgba(20,184,166,0.16),transparent_55%)]"></div>
    <div class="absolute inset-0 bg-[radial-gradient(900px_520px_at_85%_10%,rgba(255,255,255,0.08),transparent_62%)]"></div>
    <div class="absolute inset-0 bg-[radial-gradient(700px_520px_at_75%_90%,rgba(59,130,246,0.06),transparent_60%)]"></div>
    <div class="absolute inset-0 bg-gradient-to-b from-[#07070a] via-[#050508] to-black"></div>
  </div>

  {#if !booted}
    <div class="min-h-screen grid place-items-center text-zinc-400 text-sm">
      {$t('common.loading')}
    </div>
  {:else if authMode && !authed}
    <Login
      apiBase={API_BASE}
      jsonHeaders={JSON_HEADERS}
      storageKey={STORAGE_KEY}
      error={authErr}
      onSave={(k) => {
        authErr = "";
        setKey(k);
      }}
    />
  {:else}
    <div class="pt-3 px-3 sm:px-6">
      <header class="sticky top-3 z-50 ui-topbar relative">
        <div class="w-full px-3 py-3 sm:px-4">
          <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div class="flex min-w-0 items-center justify-between gap-3">
              <button
                type="button"
                class="flex min-w-0 items-center gap-3 text-left"
                on:click={() => go("dashboard")}
              >
                <span class="ui-logo shrink-0 text-semibold" aria-hidden="true">
                  <Icon icon="hugeicons:monocle" width="18" height="18" />
                </span>
                <div class="flex min-w-0 flex-col leading-tight">
                  <div class="truncate text-lg font-semibold tracking-wide text-white">{$t('app.name')}</div>
                  <div class="truncate text-xs text-zinc-400">
                    {$t('app.tagline')}
                  </div>
                </div>
              </button>

              <div class="flex shrink-0 items-center gap-2 xl:hidden">
                <LanguageSwitcher />
                {#if authMode}
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-10 h-10 rounded border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all"
                    title={$t('nav.logout')}
                    aria-label={$t('nav.logout')}
                    on:click={() => { authErr = ""; setKey(""); }}
                  >
                    <Icon icon={logoutIcon} width="18" height="18" />
                  </button>
                {/if}
                <button
                  type="button"
                  class={`${iconBtnBase} shrink-0`}
                  title={$t('common.menu')}
                  aria-label={$t('common.menu')}
                  aria-expanded={menuOpen}
                  on:click={() => (menuOpen = !menuOpen)}
                >
                  <Icon icon={menuOpen ? closeIcon : menuIcon} width="20" height="20" />
                </button>
              </div>
            </div>

            <div class="hidden min-w-0 xl:flex xl:flex-row xl:items-center xl:justify-end xl:gap-2">
              <div class="flex h-10 max-w-full items-center gap-2 overflow-x-auto">
                <div class="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3">
                  <span
                    class={`h-2.5 w-2.5 rounded-full ${
                      dashConnected ? "bg-emerald-400" : "bg-rose-400"
                    }`}
                  ></span>
                  <span class="hidden text-sm text-zinc-200/90 sm:inline">
                    {dashConnected ? $t('common.connected') : $t('common.disconnected')}
                  </span>
                </div>

                <button
                  type="button"
                  class={`${iconBtnBase} ${dashPaused ? "opacity-80" : ""}`}
                  title={dashPaused ? $t('nav.resumePolling') : $t('nav.pausePolling')}
                  on:click={togglePause}
                >
                  <Icon icon={dashPaused ? playIcon : pauseIcon} width="18" height="18" />
                </button>

                <button
                  type="button"
                  class={iconBtnBase}
                  title={$t('common.refresh')}
                  on:click={doRefresh}
                >
                  <Icon icon={refreshIcon} width="18" height="18" />
                </button>

                <div class={dividerCls}></div>
              </div>

              <div class="hidden min-w-0 overflow-hidden xl:block">
                <nav class="topnav topnav--nowrap h-10 w-full xl:w-auto">
                  <button class="topnav__tab" class:is-active={screen === "results"} on:click={() => go("results")}>
                    <Icon icon={resultsIcon} width="16" height="16" />
                    <span>{$t('nav.results')}</span>
                  </button>

                  <button class="topnav__tab" class:is-active={screen === "storage"} on:click={() => go("storage")}>
                    <Icon icon={storageIcon} width="16" height="16" />
                    <span>{$t('nav.storage')}</span>
                  </button>

                  <button class="topnav__tab" class:is-active={screen === "webhooks"} on:click={() => go("webhooks")}>
                    <Icon icon={webhookIcon} width="16" height="16" />
                    <span>{$t('nav.webhooks')}</span>
                  </button>

                  <button class="topnav__tab" class:is-active={screen === "tasks"} on:click={() => go("tasks")}>
                    <Icon icon={tasksIcon} width="16" height="16" />
                    <span>{$t('nav.tasks')}</span>
                  </button>

                  <button class="topnav__tab" class:is-active={screen === "settings"} on:click={() => go("settings")}>
                    <Icon icon={settingsIcon} width="16" height="16" />
                    <span>{$t('nav.settings')}</span>
                  </button>

                  <button class="topnav__tab" class:is-active={screen === "runtime"} on:click={() => go("runtime")}>
                    <Icon icon={runtimeIcon} width="16" height="16" />
                    <span>{$t('nav.runtime')}</span>
                  </button>
                </nav>
              </div>

              {#if authMode}
                <div class="flex h-10 items-center gap-2">
                  <div class="w-px h-6 bg-white/10 mx-2"></div>
                  <LanguageSwitcher />
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all"
                    title={$t('nav.logout')}
                    aria-label={$t('nav.logout')}
                    on:click={() => { authErr = ""; setKey(""); }}
                  >
                    <Icon icon={logoutIcon} width="18" height="18" />
                  </button>
                </div>
              {:else}
                <div class="flex h-10 items-center gap-2">
                  <div class="w-px h-6 bg-white/10 mx-2"></div>
                  <LanguageSwitcher />
                </div>
              {/if}
            </div>
          </div>

          {#if menuOpen}
            <div class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 px-0 xl:hidden">
              <nav class="grid gap-2 rounded-2xl border border-white/10 bg-black/95 p-3 shadow-[0_24px_70px_-30px_rgba(0,0,0,0.95)] backdrop-blur-xl">
                <button class="mobile-nav__tab" class:is-active={screen === "results"} on:click={() => go("results")}>
                  <Icon icon={resultsIcon} width="18" height="18" />
                  <span>{$t('nav.results')}</span>
                </button>

                <button class="mobile-nav__tab" class:is-active={screen === "storage"} on:click={() => go("storage")}>
                  <Icon icon={storageIcon} width="18" height="18" />
                  <span>{$t('nav.storage')}</span>
                </button>

                <button class="mobile-nav__tab" class:is-active={screen === "webhooks"} on:click={() => go("webhooks")}>
                  <Icon icon={webhookIcon} width="18" height="18" />
                  <span>{$t('nav.webhooks')}</span>
                </button>

                <button class="mobile-nav__tab" class:is-active={screen === "api"} on:click={() => go("api")}>
                  <Icon icon={apiIcon} width="18" height="18" />
                  <span>{$t('nav.api')}</span>
                </button>

                <button class="mobile-nav__tab" class:is-active={screen === "tasks"} on:click={() => go("tasks")}>
                  <Icon icon={tasksIcon} width="18" height="18" />
                  <span>{$t('nav.tasks')}</span>
                </button>

                <button class="mobile-nav__tab" class:is-active={screen === "settings"} on:click={() => go("settings")}>
                  <Icon icon={settingsIcon} width="18" height="18" />
                  <span>{$t('nav.settings')}</span>
                </button>

                <button class="mobile-nav__tab" class:is-active={screen === "runtime"} on:click={() => go("runtime")}>
                  <Icon icon={runtimeIcon} width="18" height="18" />
                  <span>{$t('nav.runtime')}</span>
                </button>
              </nav>
            </div>
          {/if}
        </div>
      </header>
    </div>

    {#if screen === "dashboard"}
      <Dashboard
        apiBase={API_BASE}
        apiKey={apiKey}
        authMode={authMode}
        bind:connected={dashConnected}
        bind:paused={dashPaused}
        setRefresh={(fn) => (dashRefresh = fn)}
      />
    {:else if screen === "results"}
      <Results apiBase={API_BASE} apiKey={apiKey} authMode={authMode} />
    {:else if screen === "settings"}
      <Settings apiBase={API_BASE} apiKey={apiKey} {setup} setupErr={setupErr} />
    {:else if screen === "api"}
      <Api apiBase={API_BASE} apiKey={apiKey} />
    {:else if screen === "storage"}
      <Storage apiBase={API_BASE} apiKey={apiKey} />
    {:else if screen === "tasks"}
      <Tasks apiBase={API_BASE} apiKey={apiKey} />
    {:else if screen === "runtime"}
      <Runtime apiBase={API_BASE} apiKey={apiKey} authMode={authMode} />
    {:else}
      <Webhooks apiBase={API_BASE} apiKey={apiKey} authMode={authMode} />
    {/if}
  {/if}
</div>
